import React from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode | ((props: { error: Error; reset: () => void }) => React.ReactNode)
  resetKey?: string | number
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  level?: 'page' | 'section' | 'widget'
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null })
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo.componentStack)
    this.props.onError?.(error, errorInfo)
  }

  reset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      const { fallback, level = 'widget' } = this.props
      const error = this.state.error ?? new Error('Unknown error')

      if (typeof fallback === 'function') {
        return fallback({ error, reset: this.reset })
      }
      if (fallback !== undefined) {
        return fallback
      }

      if (level === 'widget') return null

      return <ErrorFallback error={error} reset={this.reset} level={level} />
    }
    return this.props.children
  }
}

function ErrorFallback({ error, reset, level }: { error: Error; reset: () => void; level: 'page' | 'section' }) {
  const isPage = level === 'page'

  return (
    <div className={`flex flex-col items-center justify-center gap-4 text-center ${isPage ? 'min-h-[60vh] p-8' : 'p-6'}`}>
      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-red-50 text-red-500">
        <AlertTriangle className="w-6 h-6" />
      </div>
      <div className="space-y-1.5 max-w-md">
        <p className="text-sm font-medium text-foreground">
          {isPage ? 'This page encountered an error' : 'Something went wrong'}
        </p>
        <p className="text-xs text-muted-foreground font-mono break-all">{error.message}</p>
      </div>
      <button
        onClick={reset}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-background hover:bg-muted transition-colors"
      >
        <RotateCcw className="w-3 h-3" />
        Try again
      </button>
    </div>
  )
}
